import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import {
  Receipt,
  Assessment,
  CompareArrows,
  Refresh,
} from '@mui/icons-material';
import { financeService } from '../../services/financeService';
import {
  GSTR1Summary,
  GSTR3BSummary,
  GSTInputOutputSummary,
  GSTR1B2BDetail,
} from '../../types/finance';
import {
  ReportHeader,
  ExportToolbar,
  IndianAmountDisplay,
} from '../../components/finance';
import { exportToPDF, exportToExcel, formatIndianCurrency, formatDate } from '../../utils/exportUtils';
import { useCompanySettings, formatCompanyAddress } from '../../hooks/useCompanySettings';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ py: 3 }}>
      {children}
    </Box>
  );
};

const GSTReportsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const company = useCompanySettings();
  const companyName = company?.companyName || 'Your Company Name';
  const companyGstin = company?.gstin || undefined;
  const companyAddress = formatCompanyAddress(company) || undefined;

  // Return Period selector
  const getCurrentReturnPeriod = () => {
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${month}-${year}`;
  };

  // Default the Input/Output range to the current financial year (1 Apr →
  // today) so the report generates on a single click during a demo rather
  // than erroring on an empty range.
  const currentFyStart = () => {
    const t = new Date();
    const y = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1;
    return `${y}-04-01`;
  };
  const todayStr = () => new Date().toISOString().slice(0, 10);

  const [returnPeriod, setReturnPeriod] = useState(getCurrentReturnPeriod());
  const [fromDate, setFromDate] = useState(currentFyStart());
  const [toDate, setToDate] = useState(todayStr());

  // Report States
  const [gstr1Summary, setGstr1Summary] = useState<GSTR1Summary | null>(null);
  const [gstr1B2B, setGstr1B2B] = useState<GSTR1B2BDetail[]>([]);
  const [gstr3bSummary, setGstr3bSummary] = useState<GSTR3BSummary | null>(null);
  const [inputOutputSummary, setInputOutputSummary] = useState<GSTInputOutputSummary | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const fetchGSTR1 = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summary, b2b] = await Promise.all([
        financeService.getGSTR1Summary(returnPeriod),
        financeService.getGSTR1B2BDetails(returnPeriod),
      ]);
      setGstr1Summary(summary);
      setGstr1B2B(b2b);
    } catch (err: any) {
      setError(err.message || 'Failed to load GSTR-1');
    } finally {
      setLoading(false);
    }
  };

  const fetchGSTR3B = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getGSTR3BSummary(returnPeriod);
      setGstr3bSummary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load GSTR-3B');
    } finally {
      setLoading(false);
    }
  };

  const fetchInputOutput = async () => {
    if (!fromDate || !toDate) {
      setError('Please select date range');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getGSTInputOutputSummary(fromDate, toDate);
      setInputOutputSummary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load GST summary');
    } finally {
      setLoading(false);
    }
  };

  const getReturnPeriodOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const label = date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      options.push({ value: `${month}-${year}`, label });
    }
    return options;
  };

  // Export handlers for GSTR-1
  const handleGSTR1PDF = () => {
    exportToPDF('gstr1-report', `GSTR1_${returnPeriod}`, 'landscape');
  };

  const handleGSTR1Excel = () => {
    if (!gstr1Summary) return;
    const data: Record<string, unknown>[] = [];

    // Summary data
    data.push({ description: 'GSTR-1 Summary', value: '' });
    data.push({ description: 'Return Period', value: gstr1Summary.returnPeriod });
    data.push({ description: '', value: '' });
    data.push({ description: 'B2B Sales - Invoice Count', value: gstr1Summary.b2b.invoiceCount });
    data.push({ description: 'B2B Sales - Taxable Value', value: formatIndianCurrency(gstr1Summary.b2b.totalTaxableValue) });
    data.push({ description: 'B2C Large - Invoice Count', value: gstr1Summary.b2cLarge.invoiceCount });
    data.push({ description: 'B2C Large - Taxable Value', value: formatIndianCurrency(gstr1Summary.b2cLarge.totalTaxableValue) });
    data.push({ description: 'B2C Small - Taxable Value', value: formatIndianCurrency(gstr1Summary.b2cSmall.totalTaxableValue) });
    data.push({ description: '', value: '' });
    data.push({ description: 'Tax Liability Breakup', value: '' });
    data.push({ description: 'IGST', value: formatIndianCurrency(gstr1Summary.totalLiability.igst) });
    data.push({ description: 'CGST', value: formatIndianCurrency(gstr1Summary.totalLiability.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(gstr1Summary.totalLiability.sgst) });
    data.push({ description: 'CESS', value: formatIndianCurrency(gstr1Summary.totalLiability.cess) });
    data.push({ description: 'Total Liability', value: formatIndianCurrency(gstr1Summary.totalLiability.total) });

    // B2B Details
    if (gstr1B2B.length > 0) {
      data.push({ description: '', value: '' });
      data.push({ description: 'B2B Invoice Details', value: '' });
      gstr1B2B.forEach((item) => {
        data.push({
          description: `${item.invoiceNumber} - ${item.gstin}`,
          value: `Taxable: ${formatIndianCurrency(item.taxableValue)}, IGST: ${formatIndianCurrency(item.igstAmount)}, CGST: ${formatIndianCurrency(item.cgstAmount)}, SGST: ${formatIndianCurrency(item.sgstAmount)}`,
        });
      });
    }

    const columns = [
      { header: 'Description', key: 'description' },
      { header: 'Value', key: 'value' },
    ];
    exportToExcel(data, columns, `GSTR1_${returnPeriod}`, 'GSTR-1');
  };

  // Export handlers for GSTR-3B
  const handleGSTR3BPDF = () => {
    exportToPDF('gstr3b-report', `GSTR3B_${returnPeriod}`, 'portrait');
  };

  const handleGSTR3BExcel = () => {
    if (!gstr3bSummary) return;
    const data: Record<string, unknown>[] = [];

    data.push({ description: 'GSTR-3B Summary', value: '' });
    data.push({ description: 'Return Period', value: gstr3bSummary.returnPeriod });
    data.push({ description: '', value: '' });
    data.push({ description: '3.1 Outward Supplies', value: '' });
    data.push({ description: 'Taxable Value', value: formatIndianCurrency(gstr3bSummary.outwardSupplies.taxableValue) });
    data.push({ description: 'IGST', value: formatIndianCurrency(gstr3bSummary.outwardSupplies.igst) });
    data.push({ description: 'CGST', value: formatIndianCurrency(gstr3bSummary.outwardSupplies.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(gstr3bSummary.outwardSupplies.sgst) });
    data.push({ description: 'CESS', value: formatIndianCurrency(gstr3bSummary.outwardSupplies.cess) });
    data.push({ description: '', value: '' });
    data.push({ description: '4. ITC Available', value: '' });
    data.push({ description: 'IGST', value: formatIndianCurrency(gstr3bSummary.itcAvailable.igst) });
    data.push({ description: 'CGST', value: formatIndianCurrency(gstr3bSummary.itcAvailable.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(gstr3bSummary.itcAvailable.sgst) });
    data.push({ description: 'CESS', value: formatIndianCurrency(gstr3bSummary.itcAvailable.cess) });
    data.push({ description: '', value: '' });
    data.push({ description: '6.1 Tax Payable', value: '' });
    data.push({ description: 'IGST', value: formatIndianCurrency(gstr3bSummary.taxPayable.igst) });
    data.push({ description: 'CGST', value: formatIndianCurrency(gstr3bSummary.taxPayable.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(gstr3bSummary.taxPayable.sgst) });
    data.push({ description: 'CESS', value: formatIndianCurrency(gstr3bSummary.taxPayable.cess) });
    data.push({ description: 'Total Tax Payable', value: formatIndianCurrency(gstr3bSummary.taxPayable.total) });

    const columns = [
      { header: 'Description', key: 'description' },
      { header: 'Amount (₹)', key: 'value' },
    ];
    exportToExcel(data, columns, `GSTR3B_${returnPeriod}`, 'GSTR-3B');
  };

  // Export handlers for Input/Output
  const handleInputOutputPDF = () => {
    exportToPDF('input-output-report', `GST_Input_Output_${fromDate}_to_${toDate}`, 'portrait');
  };

  const handleInputOutputExcel = () => {
    if (!inputOutputSummary) return;
    const data: Record<string, unknown>[] = [];

    data.push({ description: 'GST Input/Output Summary', value: '' });
    data.push({ description: `Period: ${formatDate(inputOutputSummary.period.fromDate)} to ${formatDate(inputOutputSummary.period.toDate)}`, value: '' });
    data.push({ description: '', value: '' });
    data.push({ description: 'Output GST (Liability)', value: '' });
    data.push({ description: 'CGST', value: formatIndianCurrency(inputOutputSummary.output.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(inputOutputSummary.output.sgst) });
    data.push({ description: 'IGST', value: formatIndianCurrency(inputOutputSummary.output.igst) });
    data.push({ description: 'Total Output', value: formatIndianCurrency(inputOutputSummary.output.total) });
    data.push({ description: '', value: '' });
    data.push({ description: 'Input GST (Credit)', value: '' });
    data.push({ description: 'CGST', value: formatIndianCurrency(inputOutputSummary.input.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(inputOutputSummary.input.sgst) });
    data.push({ description: 'IGST', value: formatIndianCurrency(inputOutputSummary.input.igst) });
    data.push({ description: 'Total Input', value: formatIndianCurrency(inputOutputSummary.input.total) });
    data.push({ description: '', value: '' });
    data.push({ description: 'Net Payable', value: '' });
    data.push({ description: 'CGST', value: formatIndianCurrency(inputOutputSummary.netPayable.cgst) });
    data.push({ description: 'SGST', value: formatIndianCurrency(inputOutputSummary.netPayable.sgst) });
    data.push({ description: 'IGST', value: formatIndianCurrency(inputOutputSummary.netPayable.igst) });
    data.push({ description: 'Total Net Payable', value: formatIndianCurrency(inputOutputSummary.netPayable.total) });

    const columns = [
      { header: 'Description', key: 'description' },
      { header: 'Amount (₹)', key: 'value' },
    ];
    exportToExcel(data, columns, `GST_Input_Output_${fromDate}_to_${toDate}`, 'Input-Output');
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
          GST Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate GSTR-1, GSTR-3B and GST Input/Output summaries
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Receipt />} label="GSTR-1" iconPosition="start" />
          <Tab icon={<Assessment />} label="GSTR-3B" iconPosition="start" />
          <Tab icon={<CompareArrows />} label="Input/Output" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* GSTR-1 Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Return Period"
                size="small"
                fullWidth
                value={returnPeriod}
                onChange={(e) => setReturnPeriod(e.target.value)}
                slotProps={{ select: { native: true } }}
              >
                {getReturnPeriodOptions().map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="contained"
                onClick={fetchGSTR1}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Generate GSTR-1
              </Button>
            </Grid>
            {gstr1Summary && (
              <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleGSTR1PDF}
                  onExportExcel={handleGSTR1Excel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : gstr1Summary ? (
          <Box id="gstr1-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle="GSTR-1 Summary"
              subtitle={`Return Period: ${gstr1Summary.returnPeriod}`}
            />

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">B2B Sales</Typography>
                    <Typography variant="h6">{gstr1Summary.b2b.invoiceCount} Invoices</Typography>
                    <IndianAmountDisplay amount={gstr1Summary.b2b.totalTaxableValue} bold />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">B2C Large</Typography>
                    <Typography variant="h6">{gstr1Summary.b2cLarge.invoiceCount} Invoices</Typography>
                    <IndianAmountDisplay amount={gstr1Summary.b2cLarge.totalTaxableValue} bold />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">B2C Small</Typography>
                    <IndianAmountDisplay amount={gstr1Summary.b2cSmall.totalTaxableValue} bold />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ bgcolor: 'primary.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Total Liability</Typography>
                    <IndianAmountDisplay amount={gstr1Summary.totalLiability.total} bold size="large" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tax Breakup */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Tax Liability Breakup
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">IGST</Typography>
                  <IndianAmountDisplay amount={gstr1Summary.totalLiability.igst} bold />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">CGST</Typography>
                  <IndianAmountDisplay amount={gstr1Summary.totalLiability.cgst} bold />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">SGST</Typography>
                  <IndianAmountDisplay amount={gstr1Summary.totalLiability.sgst} bold />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">CESS</Typography>
                  <IndianAmountDisplay amount={gstr1Summary.totalLiability.cess} bold />
                </Grid>
              </Grid>
            </Paper>

            {/* B2B Details */}
            {gstr1B2B.length > 0 && (
              <Paper sx={{ mt: 3 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    B2B Invoice Details
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>GSTIN</TableCell>
                        <TableCell>Invoice No.</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Taxable Value</TableCell>
                        <TableCell align="right">IGST</TableCell>
                        <TableCell align="right">CGST</TableCell>
                        <TableCell align="right">SGST</TableCell>
                        <TableCell>POS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gstr1B2B.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{item.gstin}</TableCell>
                          <TableCell>{item.invoiceNumber}</TableCell>
                          <TableCell>{new Date(item.invoiceDate).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell align="right">
                            <IndianAmountDisplay amount={item.taxableValue} />
                          </TableCell>
                          <TableCell align="right">
                            <IndianAmountDisplay amount={item.igstAmount} />
                          </TableCell>
                          <TableCell align="right">
                            <IndianAmountDisplay amount={item.cgstAmount} />
                          </TableCell>
                          <TableCell align="right">
                            <IndianAmountDisplay amount={item.sgstAmount} />
                          </TableCell>
                          <TableCell>{item.placeOfSupply}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Receipt sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select a return period and click Generate to view GSTR-1
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* GSTR-3B Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Return Period"
                size="small"
                fullWidth
                value={returnPeriod}
                onChange={(e) => setReturnPeriod(e.target.value)}
                slotProps={{ select: { native: true } }}
              >
                {getReturnPeriodOptions().map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="contained"
                onClick={fetchGSTR3B}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Generate GSTR-3B
              </Button>
            </Grid>
            {gstr3bSummary && (
              <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleGSTR3BPDF}
                  onExportExcel={handleGSTR3BExcel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : gstr3bSummary ? (
          <Box id="gstr3b-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle="GSTR-3B Summary"
              subtitle={`Return Period: ${gstr3bSummary.returnPeriod}`}
            />

            {/* Outward Supplies */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                3.1 Outward Supplies
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell></TableCell>
                      <TableCell align="right">Taxable Value</TableCell>
                      <TableCell align="right">IGST</TableCell>
                      <TableCell align="right">CGST</TableCell>
                      <TableCell align="right">SGST</TableCell>
                      <TableCell align="right">Cess</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Outward Supplies</TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={gstr3bSummary.outwardSupplies.taxableValue} />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={gstr3bSummary.outwardSupplies.igst} />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={gstr3bSummary.outwardSupplies.cgst} />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={gstr3bSummary.outwardSupplies.sgst} />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={gstr3bSummary.outwardSupplies.cess} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* ITC Available */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                4. ITC Available
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">IGST</Typography>
                  <IndianAmountDisplay amount={gstr3bSummary.itcAvailable.igst} bold />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">CGST</Typography>
                  <IndianAmountDisplay amount={gstr3bSummary.itcAvailable.cgst} bold />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">SGST</Typography>
                  <IndianAmountDisplay amount={gstr3bSummary.itcAvailable.sgst} bold />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="caption" color="text.secondary">Cess</Typography>
                  <IndianAmountDisplay amount={gstr3bSummary.itcAvailable.cess} bold />
                </Grid>
              </Grid>
            </Paper>

            {/* Tax Payable */}
            <Card sx={{ bgcolor: 'error.50' }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  6.1 Tax Payable
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 2.4 }}>
                    <Typography variant="caption" color="text.secondary">IGST</Typography>
                    <IndianAmountDisplay amount={gstr3bSummary.taxPayable.igst} bold color="negative" />
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Typography variant="caption" color="text.secondary">CGST</Typography>
                    <IndianAmountDisplay amount={gstr3bSummary.taxPayable.cgst} bold color="negative" />
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Typography variant="caption" color="text.secondary">SGST</Typography>
                    <IndianAmountDisplay amount={gstr3bSummary.taxPayable.sgst} bold color="negative" />
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Typography variant="caption" color="text.secondary">Cess</Typography>
                    <IndianAmountDisplay amount={gstr3bSummary.taxPayable.cess} bold color="negative" />
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <IndianAmountDisplay amount={gstr3bSummary.taxPayable.total} bold size="large" color="negative" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Assessment sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select a return period and click Generate to view GSTR-3B
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Input/Output Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                fullWidth
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="To Date"
                type="date"
                size="small"
                fullWidth
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                variant="contained"
                onClick={fetchInputOutput}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Generate
              </Button>
            </Grid>
            {inputOutputSummary && (
              <Grid size={{ xs: 12, sm: 3 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleInputOutputPDF}
                  onExportExcel={handleInputOutputExcel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 2 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : inputOutputSummary ? (
          <Box id="input-output-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle="GST Input/Output Summary"
              fromDate={inputOutputSummary.period.fromDate}
              toDate={inputOutputSummary.period.toDate}
            />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: 'error.50' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Output GST (Liability)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">CGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.output.cgst} /></Grid>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">SGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.output.sgst} /></Grid>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">IGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.output.igst} /></Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Total</Typography>
                      <IndianAmountDisplay amount={inputOutputSummary.output.total} bold />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: 'success.50' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Input GST (Credit)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">CGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.input.cgst} /></Grid>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">SGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.input.sgst} /></Grid>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">IGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.input.igst} /></Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Total</Typography>
                      <IndianAmountDisplay amount={inputOutputSummary.input.total} bold />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: 'primary.50' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Net Payable
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">CGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.netPayable.cgst} /></Grid>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">SGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.netPayable.sgst} /></Grid>
                      <Grid size={{ xs: 6 }}><Typography variant="caption">IGST</Typography></Grid>
                      <Grid size={{ xs: 6 }}><IndianAmountDisplay amount={inputOutputSummary.netPayable.igst} /></Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Total</Typography>
                      <IndianAmountDisplay
                        amount={inputOutputSummary.netPayable.total}
                        bold
                        size="large"
                        color={inputOutputSummary.netPayable.total > 0 ? 'negative' : 'positive'}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CompareArrows sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select date range and click Generate to view Input/Output Summary
            </Typography>
          </Paper>
        )}
      </TabPanel>
    </>
  );
};

export default GSTReportsPage;
